import { Switch } from '@reflex/ui/components/ui/switch';
import { Badge } from '@reflex/ui/components/ui/badge';
import { SectionLabel, PageDescription } from '@reflex/ui/components/layout/Typography';
import { DescriptionHint } from '@reflex/ui/components/shared/DescriptionHint';
import { ModelChip } from '@reflex/ui/components/agent/ModelChip';
import type { AgentModelProvider, DiscoveredModel } from '@reflex/shared';

const MODELS_SECTION_DESCRIPTION =
  'Toggle individual models on or off. Disabled provider models are automatically hidden.';

export interface ModelsByProviderSectionProps {
  providers: AgentModelProvider[];
  modelsByProvider: Record<string, DiscoveredModel[]>;
  isProviderEnabled: (providerId: string) => boolean;
  isModelEnabled: (modelId: string) => boolean;
  isPending: boolean;
  hasDiscoveredModels: boolean;
  onToggleModel: (modelId: string, enabled: boolean) => void;
  readOnly?: boolean;
}

export function ModelsByProviderSection({
  providers,
  modelsByProvider,
  isProviderEnabled,
  isModelEnabled,
  isPending,
  hasDiscoveredModels,
  onToggleModel,
  readOnly = false,
}: ModelsByProviderSectionProps) {
  return (
    <div className="border-t pt-4">
      <div className="mb-3 flex items-center gap-1">
        <SectionLabel className="mb-0">Configure Models</SectionLabel>
        <DescriptionHint className="sm:hidden" ariaLabel="About this section">
          {MODELS_SECTION_DESCRIPTION}
        </DescriptionHint>
      </div>
      <PageDescription className="mb-3 max-sm:hidden text-xs">
        {MODELS_SECTION_DESCRIPTION}
      </PageDescription>
      {providers.map((provider) => {
        const models = modelsByProvider[provider.id] ?? [];
        const providerEnabled = isProviderEnabled(provider.id);
        if (models.length === 0) return null;
        const enabledCount = models.filter((m) => isModelEnabled(m.id) && providerEnabled).length;
        return (
          <div key={provider.id} className="mb-4 last:mb-0">
            <div className="flex items-center gap-2 mb-2 py-1 px-2 bg-muted/30 rounded">
              <SectionLabel as="span" className="text-xs">
                {provider.displayName}
              </SectionLabel>
              <Badge variant="outline" className="text-2xs">
                {enabledCount} / {models.length} enabled
              </Badge>
              {!providerEnabled && (
                <Badge variant="secondary" className="text-2xs">
                  provider disabled
                </Badge>
              )}
            </div>
            <div className="space-y-1.5 pl-2">
              {models.map((model) => {
                const enabled = isModelEnabled(model.id);
                return (
                  <div
                    key={model.id}
                    className="flex items-center justify-between gap-3 py-1 rounded hover:bg-muted/30 pl-2"
                  >
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <ModelChip model={model.id} name={model.displayName} />
                    </div>
                    <Switch
                      checked={enabled && providerEnabled}
                      onCheckedChange={(checked) => onToggleModel(model.id, checked)}
                      disabled={readOnly || isPending || !providerEnabled}
                      aria-label={`Toggle ${model.displayName}`}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
      {!hasDiscoveredModels && (
        <PageDescription className="text-xs italic">
          No models are available for this agent.
        </PageDescription>
      )}
    </div>
  );
}
